import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, H1, H2, H3, H4, Body, BodySm, Input, Text } from '@/components/ui';
import { NotLegalIcon } from '@/components/ui/NotLegalIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DEMO_USER, useDemoAuth } from '@/lib/demo-auth';

interface Transaction {
  id: string;
  type: 'usage' | 'purchase' | 'bonus';
  description: string;
  amount: number;
  date: string;
}

interface UsageData {
  date: string;
  tokens: number;
}

export function DashboardPage() {
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === 'true';
  const { user: demoUser, logoutDemo } = useDemoAuth();
  const [giftCode, setGiftCode] = useState('');
  const [tokenBalance, setTokenBalance] = useState(0);
  const [usageData, setUsageData] = useState<UsageData[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [autoTopupEnabled, setAutoTopupEnabled] = useState(false);
  const [totalChats, setTotalChats] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);

  // Load data based on demo mode
  useEffect(() => {
    if (isDemo) {
      setTokenBalance(DEMO_USER.tokenBalance);
      setSubscriptionTier(DEMO_USER.subscriptionTier);
      setUsageData([
        { date: 'Jan 1', tokens: 1200 },
        { date: 'Jan 2', tokens: 900 },
        { date: 'Jan 3', tokens: 1600 },
        { date: 'Jan 4', tokens: 800 },
        { date: 'Jan 5', tokens: 2100 },
        { date: 'Jan 6', tokens: 1800 },
        { date: 'Jan 7', tokens: 2450 },
      ]);
      setTransactions([
        { id: '1', type: 'usage', description: 'Chat messages', amount: -450, date: '2 hours ago' },
        { id: '2', type: 'purchase', description: 'Starter Token Pack', amount: 2500, date: '1 day ago' },
        { id: '3', type: 'usage', description: 'Document analysis', amount: -320, date: '3 days ago' },
        { id: '4', type: 'bonus', description: 'Daily bonus', amount: 50, date: '4 days ago' },
      ]);
      setTotalChats(47);
      setTotalDocuments(3);
    } else {
      setTokenBalance(2450);
      setSubscriptionTier('basic');
      setUsageData([
        { date: 'Today', tokens: 450 },
        { date: 'Yesterday', tokens: 1200 },
        { date: 'Jan 14', tokens: 800 },
        { date: 'Jan 13', tokens: 1600 },
        { date: 'Jan 12', tokens: 950 },
        { date: 'Jan 11', tokens: 2100 },
        { date: 'Jan 10', tokens: 1800 },
      ]);
      setTransactions([
        { id: '1', type: 'usage', description: 'Chat messages', amount: -150, date: 'Just now' },
        { id: '2', type: 'bonus', description: 'Daily bonus', amount: 50, date: '5 hours ago' },
        { id: '3', type: 'purchase', description: 'Starter Token Pack', amount: 2500, date: '2 days ago' },
      ]);
      setTotalChats(12);
      setTotalDocuments(2);
    }
  }, [isDemo, demoUser]);

  const handleRedeemGift = () => {
    if (giftCode.trim()) {
      alert(`Would redeem gift code: ${giftCode}`);
      setGiftCode('');
    }
  };

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <header className="bg-white border-b border-surface-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <NotLegalIcon name="zap" className="w-8 h-8 text-primary-600" />
            <span className="text-xl font-bold text-surface-900">notlegal.chat</span>
            {isDemo && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                Demo
              </span>
            )}
          </Link>
          <div className="flex items-center space-x-4">
            {isDemo ? (
              <Link to="/" onClick={logoutDemo} className="flex items-center space-x-2 text-surface-500 hover:text-red-600 transition-colors">
                <NotLegalIcon name="logout" className="w-4 h-4" />
                <span>Exit Demo</span>
              </Link>
            ) : (
              <>
                <Button variant="ghost" size="sm" icon={<NotLegalIcon name="bell" className="w-4 h-4" />}>
                  Notifications
                </Button>
                <img
                  src={DEMO_USER.avatarUrl}
                  alt="User"
                  className="w-8 h-8 rounded-full"
                />
              </>
            )}
          </div>
        </div>
      </header>

      <main className="py-8 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Welcome Section */}
          <div className="mb-8">
            <H1>Welcome back, {isDemo ? DEMO_USER.displayName : 'Legal Maverick'}!</H1>
            <Body className="text-surface-600">Here's your questionable legal journey overview.</Body>
          </div>

          {/* Stats Grid */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card padding="lg">
              <div className="flex items-center justify-between">
                <div>
                  <BodySm className="text-surface-500">Token Balance</BodySm>
                  <H2 className="mt-1">{tokenBalance.toLocaleString()}</H2>
                </div>
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                  <NotLegalIcon name="coin" className="w-6 h-6 text-primary-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <Link to="/pricing">
                  <Button size="sm" icon={<NotLegalIcon name="creditCard" className="w-4 h-4" />}>
                    Buy More
                  </Button>
                </Link>
              </div>
            </Card>

            <Card padding="lg">
              <div className="flex items-center justify-between">
                <div>
                  <BodySm className="text-surface-500">Current Plan</BodySm>
                  <H2 className="mt-1 text-primary-600 capitalize">{subscriptionTier}</H2>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <NotLegalIcon name="trending" className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <div className="mt-4">
                <Link to="/pricing">
                  <Button variant="ghost" size="sm">
                    Upgrade <NotLegalIcon name="chevronRight" className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </Card>

            <Card padding="lg">
              <div className="flex items-center justify-between">
                <div>
                  <BodySm className="text-surface-500">Total Chats</BodySm>
                  <H2 className="mt-1">{totalChats}</H2>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <NotLegalIcon name="message" className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className="mt-4">
                <Link to="/chat?demo=true">
                  <Button variant="ghost" size="sm">
                    View All <NotLegalIcon name="chevronRight" className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </Card>

            <Card padding="lg">
              <div className="flex items-center justify-between">
                <div>
                  <BodySm className="text-surface-500">Documents</BodySm>
                  <H2 className="mt-1">{totalDocuments}</H2>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <NotLegalIcon name="fileDownload" className="w-6 h-6 text-orange-600" />
                </div>
              </div>
              <div className="mt-4">
                <Link to="/documents">
                  <Button variant="ghost" size="sm">
                    Manage <NotLegalIcon name="chevronRight" className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </Card>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Usage Chart */}
            <Card className="lg:col-span-2" padding="lg">
              <H3 className="mb-6">Token Usage This Week</H3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={usageData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="date" stroke="#71717a" fontSize={12} />
                    <YAxis stroke="#71717a" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e4e4e7',
                        borderRadius: '12px',
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="tokens"
                      stroke="#0ea5e9"
                      strokeWidth={3}
                      dot={{ fill: '#0ea5e9', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <BodySm className="mt-4 text-surface-500 text-center">
                Total used this week: {usageData.reduce((sum, d) => sum + d.tokens, 0).toLocaleString()} tokens
              </BodySm>
            </Card>

            {/* Gift Card Redemption */}
            <Card padding="lg">
              <H3 className="mb-4 flex items-center">
                <NotLegalIcon name="gift" className="w-5 h-5 mr-2 text-primary-600" />
                Redeem Gift Card
              </H3>
              <Input
                placeholder="Enter gift code"
                value={giftCode}
                onChange={(e) => setGiftCode(e.target.value)}
              />
              <Button className="w-full mt-4" icon={<NotLegalIcon name="refresh" className="w-4 h-4" />} onClick={handleRedeemGift}>
                Redeem
              </Button>
              <BodySm className="mt-4 text-center text-surface-500">
                Gift cards are like surprise tokens. But legal.
              </BodySm>
            </Card>
          </div>

          {/* Auto-Topup Settings */}
          <Card className="mt-8" padding="lg">
            <div className="flex items-center justify-between mb-6">
              <div>
                <H3>Auto-Topup Settings</H3>
                <Body className="text-surface-500 mt-1">
                  Automatically add tokens when your balance gets low.
                </Body>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={autoTopupEnabled}
                  onChange={(e) => setAutoTopupEnabled(e.target.checked)}
                />
                <div className="w-11 h-6 bg-surface-200 rounded-full peer peer-checked:bg-primary-600 peer-focus:ring-4 peer-focus:ring-primary-100 transition-colors"></div>
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
              </label>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <BodySm className="text-surface-500 mb-2">Top up when tokens drop to</BodySm>
                <Input placeholder="500 tokens" type="number" />
              </div>
              <div>
                <BodySm className="text-surface-500 mb-2">Add this many tokens</BodySm>
                <Input placeholder="5000" type="number" />
              </div>
              <div className="flex items-end">
                <Button variant="secondary" className="w-full">
                  Save Settings
                </Button>
              </div>
            </div>
          </Card>

          {/* Recent Transactions */}
          <Card className="mt-8" padding="lg">
            <H3 className="mb-6">Recent Transactions</H3>
            <div className="space-y-4">
              {transactions.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between py-3 border-b border-surface-100 last:border-0">
                  <div>
                    <div className="font-medium text-surface-700">{tx.description}</div>
                    <div className="text-sm text-surface-400">{tx.date}</div>
                  </div>
                  <div className={`font-semibold ${tx.amount > 0 ? 'text-green-600' : 'text-surface-900'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString()} tokens
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Demo Mode Notice */}
          {isDemo && (
            <Card className="mt-8 bg-amber-50 border-amber-200" padding="lg">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <NotLegalIcon name="user" className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <H4 className="text-amber-800">Demo Mode Active</H4>
                  <Body className="text-amber-700 mt-1">
                    You're viewing the app in demo mode with simulated data. Sign up for a real account to get 500 free tokens and start chatting!
                  </Body>
                  <div className="mt-4 flex space-x-4">
                    <Link to="/sign-up">
                      <Button size="sm">Sign Up for Real</Button>
                    </Link>
                    <Link to="/" onClick={logoutDemo}>
                      <Button variant="ghost" size="sm">Exit Demo</Button>
                    </Link>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Settings Link */}
          <div className="mt-8 flex justify-center">
            <Link to="/settings">
              <Button variant="ghost" icon={<NotLegalIcon name="settings" className="w-4 h-4" />}>
                Account Settings
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
